/*
 * Formatting for the Morrisons dashboard — British English, as the design
 * writes it: "619,875", "88.7%", "2 Nov", "Tuesday 24 November 2026".
 * A missing figure is "—", never 0.
 */

export function fmtInt(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return Math.round(n).toLocaleString('en-GB');
}

/** 88.66 → "88.7%". */
export function fmtPct(n: number | null | undefined, digits = 1): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n.toLocaleString('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`;
}

/** 3.43 → "3.4". */
export function fmtDec(n: number | null | undefined, digits = 1): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** Axis ticks: 12000 → "12k", 1500000 → "1.5M". */
export function fmtCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toLocaleString('en-GB', { maximumFractionDigits: 1 })}M`;
  if (n >= 1000) return `${(n / 1000).toLocaleString('en-GB', { maximumFractionDigits: 1 })}k`;
  return fmtInt(n);
}

/** part ÷ whole as a percentage; null without a base. */
export function share(part: number | null | undefined, whole: number | null | undefined): number | null {
  if (part == null || whole == null || whole <= 0) return null;
  return (part / whole) * 100;
}

/** a ÷ b; null without a base. */
export function ratio(a: number | null | undefined, b: number | null | undefined): number | null {
  if (a == null || b == null || b <= 0) return null;
  return a / b;
}

/** A calendar day written out: "long" → "Tuesday 24 November 2026", "dayMonth" → "24 Nov", "short" → "Tue 24 Nov". */
export function fmtDay(iso: string, style: 'long' | 'short' | 'dayMonth'): string {
  const d = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return '—';
  const opts: Intl.DateTimeFormatOptions =
    style === 'long'
      ? { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }
      : style === 'short'
        ? { weekday: 'short', day: 'numeric', month: 'short' }
        : { day: 'numeric', month: 'short' };
  return new Intl.DateTimeFormat('en-GB', { ...opts, timeZone: 'UTC' }).format(d);
}

/** Campaign window → "2 Nov → 24 Dec 2026" (both years when they differ). */
export function fmtPeriod(start: string, end: string): string {
  const year = (iso: string) => iso.slice(0, 4);
  const from = `${fmtDay(start, 'dayMonth')}${year(start) === year(end) ? '' : ` ${year(start)}`}`;
  return `${from} → ${fmtDay(end, 'dayMonth')} ${year(end)}`;
}

/** ISO instant → "14:05:12" in the campaign's timezone. */
export function fmtClock(iso: string | null | undefined, timeZone: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(d);
}

/** Round "nice" ceiling for a bar axis on 1 / 2 / 5 × 10ⁿ steps. */
export function niceScale(max: number, target = 4): { max: number; step: number } {
  if (max <= 0) return { max: 1, step: 1 };
  const raw = max / target;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = Math.max(1, [1, 2, 5, 10].map((m) => m * pow).find((s) => s >= raw) ?? 10 * pow);
  return { max: Math.ceil(max / step) * step, step };
}
